import React, { useMemo } from 'react'
import { Route, Router, Switch } from 'react-router'
import './App.css'
import Axios from 'axios'
import ProtectedRoute, { OnlyAnonymousRoute } from './ProtectedRoute'
import { Home } from './Paginas/Home'
import { tokenStorage } from './TokenStorage'
import { createBrowserHistory } from 'history'
import { Container } from 'react-bootstrap'
import { History } from 'history'
import preval from 'preval.macro'
import { RedirectEventCallbackContext, useRedirectEventCallbackContextState } from "BankSAHooks"
import 'loaders.css'
import { storageFactory } from 'storage-factory'
import NewNavbar from 'Navbar'
import BankSALogin from 'Paginas/BankSALogin'
import { InstalarPWAContext } from 'utils/InstalarPWAContext'
import { BlockUIProvider, useBlockUI } from 'Context/BlockUIContext'
import { toDate } from 'Utilidades'
import { SelectDeclarante } from 'Paginas/SelectDeclarante'
import Lotes from 'Paginas/Lotes'
import AltaPedidoGuia from 'Paginas/AltaPedidoGuia'
import { ErrorModalProvider } from 'Context/ErrorModalContext'
import FormularioF2241 from 'Paginas/FormularioF2441'

export class SesionPerdidaError extends Error {
  innerError: Error | undefined
  constructor(message?: string, innerError?: Error) {
    // Pasa los argumentos restantes (incluidos los específicos del proveedor) al constructor padre
    super(message)
    // Mantiene un seguimiento adecuado de la pila para el lugar donde se lanzó nuestro error (solo disponible en V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, SesionPerdidaError)
    }

    this.name = 'SesionPerdidaError'
    this.innerError = innerError
  }
}

function getUrlApiAdv() {
  if (process.env.NODE_ENV === 'development') {
    //return 'http://200.85.188.24:8084/'
    //return 'http://192.168.2.91:8084/'
    return 'https://test.apiadv.sintia.com.ar/'
    //return 'https://apiadv.sintia.com.ar/'

  } else if (process.env.REACT_APP_BUILD === 'dev') {
    return 'https://test.apiadv.sintia.com.ar/'
    //return 'https://apiadv.sintia.com.ar/'
  } else {
    //return 'https://test.apiadv.sintia.com.ar/'
    return 'https://apiadv.sintia.com.ar/'
  }
}

function getUrlPSAD() {
  if (process.env.NODE_ENV === 'development') {
    return 'https://testapipsad.alpha2000.com.ar/api'
  } else if (process.env.REACT_APP_BUILD === 'dev') {
    return 'https://testapipsad.alpha2000.com.ar/api'
  } else {
    return 'https://apipsad.banksa.com.ar/api'
  }
}

export const urlAPIPSAD = getUrlPSAD()
export const urlAPIAdvanced = getUrlApiAdv()
export const urlMantenimiento = 'https://testapipsad.alpha2000.com.ar/api'

const buildDate = preval`module.exports = new Date()`
const basename = undefined

export const loginClient = Axios.create({
  baseURL: urlAPIPSAD
})

const client = Axios.create({
  baseURL: urlAPIPSAD,
})

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

export const userClient = client

const clientAdvanced = Axios.create({
  baseURL: urlAPIAdvanced,
})

client.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error)
)

export const userClientAdvanced = clientAdvanced

export const AppContext = React.createContext<AppContextState>({
  cerrarSesion: () => { },
})

interface AppContextState {
  cerrarSesion: () => void,
}

let appContextState: AppContextState
let browserHistory: History<unknown> | undefined = undefined
function App() {
  const storage = storageFactory(() => localStorage)
  const { showBlock, hideBlock } = useBlockUI()
  const redirectCallbackContextState = useRedirectEventCallbackContextState()
  browserHistory = useMemo(() => createBrowserHistory({
    basename: basename,
    getUserConfirmation: (_, routerCallback) => redirectCallbackContextState.runAfterUserConfirmation(routerCallback)
  }), []) //eslint-disable-line

  appContextState = {
    cerrarSesion: () => {
      tokenStorage.setAccessToken('')
      tokenStorage.setRefreshToken('')
      storage.removeItem('declaranteSeleccionado')
      window.location.replace('/login')
    },
  }

  client.interceptors.response.use(
    (response) => response,
    (error) => {
      if (error.response?.status === 401) {
        localStorage.removeItem('accessToken')
        localStorage.removeItem('declaranteSeleccionado')
        if (window.location.pathname !== '/login') {
          browserHistory?.push('/login')
        }
      }
      return Promise.reject(error)
    }
  )

  async function cerrarSesion() {
    try {
      const redirectCallback = redirectCallbackContextState.getCurrentRedirectCallback()
      if (redirectCallback) {
        const continuar = await redirectCallback()
        if (!continuar) {
          return
        }
        redirectCallbackContextState.setCurrentRedirectCallback(null)
      }
      showBlock('Cerrando sesión...')
      await client.post('/Account/LogOut')
      appContextState.cerrarSesion()
    } catch (error) {
      console.error('Error al cerrar sesión', error)
      appContextState.cerrarSesion()
    } finally {
      hideBlock()
    }
  }

  return (
    <div className="d-flex flex-column min-vh-100">
      <ErrorModalProvider>
        <InstalarPWAContext>
          <BlockUIProvider>
            <AppContext.Provider value={appContextState}>
              <RedirectEventCallbackContext.Provider value={redirectCallbackContextState}>
                <Router history={browserHistory}>
                  {tokenStorage.getAccessToken() && tokenStorage.getUserName() && <NewNavbar
                    storage={storage}
                    cerrarSesion={cerrarSesion}
                  />}
                  <Container fluid className="mt-2 justify-content-md-center">
                    <div>
                      <Switch>
                        <OnlyAnonymousRoute path="/login" component={BankSALogin} />

                        <ProtectedRoute
                          exact
                          path="/selectDeclarante"
                          component={SelectDeclarante}
                        />

                        <ProtectedRoute
                          exact
                          path="/"
                          component={Home}
                          debeTenerDeclarante
                        />

                        <ProtectedRoute
                          exact
                          path="/lotes"
                          component={Lotes}
                          debeTenerDeclarante
                        />

                        <ProtectedRoute
                          exact
                          path="/formularioF2441"
                          component={FormularioF2241}
                          debeTenerDeclarante
                        />

                        <ProtectedRoute
                          exact
                          path="/altaPedidoGuia"
                          component={AltaPedidoGuia}
                          debeTenerDeclarante
                        />

                        <Route path="*" component={NotFoundComponent} />
                      </Switch>
                    </div>
                  </Container>
                </Router>
              </RedirectEventCallbackContext.Provider>
            </AppContext.Provider>
          </BlockUIProvider>
        </InstalarPWAContext>
      </ErrorModalProvider>
      <footer className="mt-auto text-light bg-dark p-2">
        <div className="footer-container">
          <p className="footer-text">
            Bank S.A. - Rivadavia 789, 4to piso CABA, AR - Versión {process.env.REACT_APP_VERSION} - Fecha {toDate(buildDate)}
          </p>
          <img
            alt="Alpha2000"
            src="/LogoAlpha2000.png"
            className="footer-logo"
          />
        </div>
      </footer>


    </div>
  )
}

export default App

export function NotFoundComponent() {
  return (<div>
    <p className="lead">No se encuentra la página</p>
  </div>)
}